"use client";

// assets imports
import IconEye from "@/assets/icons/eye.svg";
import WpIcon from "@/assets/icons/whatsapp.svg";
import ClSwatch from "@/assets/images/cl-swatch.webp";

import { LanguageCodeEnum } from "@/api/saleor/generated/graphql";
import { getLiveVariantDetailsClient } from "@/api/saleor/services/variantClient";
import { addItemsToCart, createCart } from "@/api/saleor/services/checkout";
import {
  addToWishlist,
  createWishlist,
  deleteWishlistLineItem,
} from "@/api/saleor/services/wishlist";
import { CreateDataAttribute, createDataAttribute } from "@/api/sanity/nextSanity";
import { getAlgoliaIndexName, getRecommendationProducts } from "@/apiUtils/algoliaUtils";
import {
  algoliaAddToCartWithQueryClient,
  algoliaConversionEventClient,
  algoliaWishlistConversionClient,
} from "@/apiUtils/algoliaUtils/indexClient";
import { algoliaEventParam, eventParam } from "@/apiUtils/algoliaUtils/constants";
import { algoliaViewedEventClient } from "@/apiUtils/algoliaUtils/indexClient";
import { RecommendProductItem } from "@/apiUtils/algoliaUtils/types";
import { analyticEvents, handlePostAnalytics } from "@/apiUtils/analyticsUtils";
import {
  getCartCount,
  getCartIdByLocale,
  getCountryCodeFromLocale,
  getWishlistIdByLocale,
  hasArrayElements,
  hasLensPower,
  isContactLensProduct,
} from "@/apiUtils/commonUtils";
import {
  analyticBuyFlows,
  analyticEventSources,
  analyticEventTypes,
  cartErrors,
  fitInfo,
  MetaDataKeys,
  parentCategories,
  pdpInternalUrlQueryParams,
  previewConstants,
  productDetails,
  ProductType,
  ProductTypes,
  SaleorAttributes,
  whatsappLinks,
  LENS_FEATURES_ATTRIBUTES,
} from "@/apiUtils/constants";

import {
  getFromCookieMetadataSA,
  setInCookieMetadataSA,
} from "@/apiUtils/cookieDataUtilsServerActions";
import {
  getAttributeValue,
  getAttributeValueAndLabel,
  buildProductPriceInfo,
  groupByAttributes,
} from "@/apiUtils/productUtils";
import HeartIcon from "@/assets/icons/heartIcon.svg";
import NoImageSrc from "@/assets/images/no_image.svg";
import {
  CheckoutResponse,
  ClipOnStyleOption,
  LivePreviewProps,
  LiveVariantDetails,
  PdpContent,
  PdpContentResponse,
  Product,
  ProductAttributeOverride,
  ProductDetailsTabs,
  VariantItemPdp,
  WishlistCreateResponse,
} from "@/common/lib/types";
import { CLink } from "@/components/common/customLink/CustomLink";
import { cn } from "@/lib/tailwind";
import ProductSizing from "@/components/pdp/ProductSizing/ProductSizingV1";
import { useCustomToast } from "@/hooks/useCustomToast";
import usePdpQueryParams from "@/hooks/usePdpQueryParams";
import useSearchParams from "@/hooks/useSearchParams";
import { useGlobalStore } from "@/store/store";
import {
  addNewCartWithMultiVariantSupport,
  getAttributeValue as getVarientAttributeValue,
} from "@/utils/cart.utils";
import { getCloudinaryURL } from "@/utils/cloudinary";
import {
  fillSlot,
  getChannelIdClient,
  getChannelIdInClient,
  getFieldValue,
  getLangFromLocale,
  isNonTransactionalCountry,
} from "@/utils/common";
import { endPageTransition, startPageTransition } from "@/utils/pageLoader.utils";
import {
  extractProductDetails,
  extractValueBySlug,
  extractVariantAttributes,
  extractBundleVariantAttributes,
  formatMeasurement,
  generateAltText,
  getPowerList,
  getRequiredAttributes,
  IPowerList,
  parseJsonGetValue,
  isContactLensInStock,
} from "@/utils/product.utils";
import Image from "next/image";
import { ReadonlyURLSearchParams } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import BenefitSection from "../home/benefitSection/BenefitSection";
import ProductBadge from "../productBadge/ProductBadge";
import ProductCarousel from "../productCarousel/ProductCarousel";
import ProductPrice from "../productPrice/ProductPrice";
import Breadcrumb, { BreadcrumbItem } from "../ui/Breadcrumb/Breadcrumb";
import Spacer from "../ui/spacer/Spacer";
import AddToCart from "./AddToCart";
import CNCBadge from "./CNCBadge";
import LensePackSize, { LensPackItem } from "./LensePackSize";
import LensPowerSelection from "./LensPowerSelection/LensPowerSelection";
import PaymentOptions from "./PaymentOptions";
import PdpBanner from "./PdpBanner";
import ProductDetailsTab from "./ProductDetailsTab/ProductDetailsTab";
import ProductDisplayCarousel from "./ProductDisplayCarousel";
import PromotionPreviewNotice from "./PromotionPreviewNotice";
import { getDisplayPriceAmount, isFuturePromotionPreviewPricing } from "./promotionPreviewUtils";
import ProductPromotion from "./ProductPromotions";
import ProductTags from "./ProductTags";
import PairEyewearSwatches from "./ProductVariants/PairEyewearSwatches";
import ProductColorVariants, { variantData } from "./ProductVariants/ProductColorVariants";
import { CNCBadgeTypes, ImageType, ProductDetailsTabData } from "./types";
import USPElements from "./UspElements";
import VolumeSelector from "./VolumeSelector";
import { useRouter } from "next/navigation";
import { getVariantsByProductIdClient } from "@/api/saleor/services/topFilterClient";
import { getVariantsBySkus } from "@/api/saleor/services";

interface PdpLayoutDetailsProps {
  locale: string;
  product: Product;
  channel: string;
  lang: "ar" | "en";
  variantId: string;
  transactional: boolean;
  layoutDetails: PdpContent;
  isLens: boolean;
  pdpLayout: PdpContentResponse;
  distinctTopsStyles: ClipOnStyleOption[];
  initialRecommendations?: RecommendProductItem[];
}

interface BadgeItem {
  name: string;
  translation: {
    name: string;
  };
}

type productDetailsType =
  | "about"
  | "productDetails"
  | "shippingAndReturn"
  | "giftPackaging"
  | "sizeAndFit"
  | "customTab"
  | "guideTab"
  | "careTab"
  | "faqTab";
interface tabData {
  label: string;
  key: string;
  type: productDetailsType;
  data: any;
}

const PdpLayoutDetails = ({
  isLens,
  channel,
  lang,
  locale,
  product,
  transactional,
  variantId,
  pdpLayout,
  layoutDetails,
  distinctTopsStyles,
  initialRecommendations,
}: PdpLayoutDetailsProps) => {
  const router = useRouter();
  const translations = layoutDetails.translations;
  const [selectedvariantId, setSelectedVariantId] = useState<string>(variantId);
  const [currentProduct, setCurrentProduct] = useState(product);
  const variantIdx =
    currentProduct?.variants?.findIndex((item) => item.id === selectedvariantId) ?? -1;
  const [selectedVariant, setSelectedVariant] = useState<VariantItemPdp | undefined>(
    currentProduct?.variants?.find((item) => item.id === selectedvariantId),
  );

  const selectedVariantRef = useRef(selectedVariant);
  const isAddingToCartRef = useRef(false);
  const [selectedQuantity, setSelectedQuantity] = useState<number>(1);
  const [hasPowerSelected, setHasPowerSelected] = useState(false);
  const [hasPowerSelectionError, setPowerSelectionError] = useState(false);
  const [recommendedProducts, setRecommendedProducts] = useState<RecommendProductItem[]>(
    initialRecommendations ?? [],
  );
  const recommendationsVariantRef = useRef(initialRecommendations ? variantId : undefined);

  // secondary variant
  const [secondaryVariant, setSecondaryVariant] = useState<VariantItemPdp | undefined>(undefined);
  const [secondaryVariantQuantity, setSecondaryVariantQuantity] = useState<number>(1);

  const [defaultVariantIdx, setDefaultVariant] = useState(
    currentProduct?.variants?.findIndex((v) => v.id === variantId),
  );

  const [localSearchParams, setLocalSearchParams] = useState<{
    source?: string;
    sourceId?: string;
    hitIndex?: number;
    queryID?: string;
  }>({});

  const productType = product?.productType?.slug ?? "";

  const { showToast } = useCustomToast();
  const { searchParams } = useSearchParams();

  const {
    cartCount,
    wishlistedProducts,
    user,
    addProductToWishlist,
    removeProductFromWishlist,
    updateCartCount,
    isAuthenticated,
  } = useGlobalStore((state) => state);

  const [isWishlisted, setIsWishlisted] = useState(wishlistedProducts?.includes(selectedvariantId));

  const variantAttributes = extractVariantAttributes(lang, currentProduct, variantIdx);
  const attributes = getRequiredAttributes(variantAttributes, [
    "title",
    "parent_brand",
    "product_image",
    "model_image",
    "cl_product_image",
    "model",
    "colorCode",
    "sapEAN",
    "shape",
    "frameMaterialEnum",
    "lenseColor",
    "polarized",
    "frameTypeEnum",
    "mirror",
    // "frameLength",
    // "fsShape",
    "bridgeSize",
    "lensSize",
    "tSize",
    "fStyleEnum",
    "vto_enabled",
    "fcCodeEnum",
    "feature1",
    "feature2",
    "feature3",
    "feature4",
    "description",
    "clColorEnum",
    "clPerPackSize",
    "clPerPack",
    "clMatEnum",
    "clWater",
    "clDiameter",
    "clBaseCurve",
    "uvBlock",
    "clUsage",
    "windEnum",
    "clPower",
    "acModel",
    "accColorFamilyEnumCode",
    "acGenderEnumCode",
    "acPrdCatgEnumCode",
    "acPrdMatEnumCode",
    "geo_fit",
    "frame_lens_material",
    "photochromic",
    "gradient",
    "anti_reflective",
    "printed",
    "blue_light_cut",
    "solid_color",
    "clipon_framecolor",
    "clipon_lenscolor",
    "clipon_style",
    "hard_coating",
    "uv_block",
    "material_composition",
    "frame_finish",
    "clip_on_1",
    "clip_on_2",
    "frame_pattern",
    "flexible",
    "expandable",
    "armless",
    "temple_material",
    "temple_tip_material",
    "temple_color",
    "temple_tip_color",
    "hinges_material",
    "oxygen_permeability",
    "manufacturer",
    "lens_features",
    "mirrored",
    "rxable",
    "parent_brand",
    "temp_clipon_style",
    "prizm",
    "frame_design",
    "theme",
    "collectionName"
  ]);

  const { updatePdpQueryParams, getWindowQueryParams } = usePdpQueryParams();

  // Helper function to get power-aware override value for clPerPackSize
  const getClPerPackSizeOverride = useMemo(() => {
    return (productId: string, isPowered: boolean): number | null => {
      const overrides = pdpLayout?.productAttributeOverrides;
      if (overrides && productId) {
        const override = overrides.find((o) => o.productId === productId);
        if (override) {
          const value = isPowered ? override.clPerPackSize : override.clPerPackSizeZeroPower;
          if (value !== undefined && value !== null) {
            return value;
          }
        }
      }
      return null;
    };
  }, [pdpLayout?.productAttributeOverrides]);

  // Get override value for current product based on default variant's power
  const clPerPackSizeOverride = useMemo(() => {
    const isPowered = parseFloat(attributes?.clPower?.value) !== 0;
    return getClPerPackSizeOverride(product?.id ?? "", isPowered);
  }, [product?.id, getClPerPackSizeOverride, attributes?.clPower?.value]);

  // Initialize selectedPack with override if available
  const initialPackSize = useMemo(() => {
    if (clPerPackSizeOverride !== null) {
      return clPerPackSizeOverride.toString();
    }
    return attributes?.clPerPackSize?.value;
  }, [clPerPackSizeOverride, attributes?.clPerPackSize?.value]);

  const [selectedPack, setSelectedPack] = useState<string>(initialPackSize);

  // Update selectedPack when override or attributes change
  useEffect(() => {
    if (clPerPackSizeOverride !== null) {
      setSelectedPack(clPerPackSizeOverride.toString());
    } else if (attributes?.clPerPackSize?.value) {
      setSelectedPack(attributes.clPerPackSize.value);
    }
  }, [clPerPackSizeOverride, attributes?.clPerPackSize?.value]);
  const isOutOfStock = useMemo(() => {
    if (isLens) {
      if (hasPowerSelected) {
        // A specific power was selected — check just those variants
        const isPrimaryOutOfStock = (selectedVariant?.quantityAvailable ?? 0) <= 0;
        const isSecondaryOutOfStock = secondaryVariant
          ? (secondaryVariant?.quantityAvailable ?? 0) <= 0
          : false;
        return isPrimaryOutOfStock || isSecondaryOutOfStock;
      }
      // No power selected yet — check if the selected COLOR has any in-stock power variant.
      // If no color is selected yet, fall back to checking all variants.
      const selectedColorValue = extractValueBySlug(lang, selectedVariant?.attributes ?? [], "clColorEnum");
      const relevantVariants = selectedColorValue
        ? currentProduct?.variants?.filter((v) => {
            const colorAttr = extractValueBySlug(lang, v?.attributes ?? [], "clColorEnum");
            return colorAttr === selectedColorValue;
          })
        : currentProduct?.variants;

      return !relevantVariants?.some((v) => (v.quantityAvailable ?? 0) > 0);
    }
    return (
      (selectedVariant?.quantityAvailable ?? 0) <= 0 ||
      (secondaryVariant ? (secondaryVariant?.quantityAvailable ?? 0) <= 0 : false)
    );
  }, [
    isLens,
    hasPowerSelected,
    selectedVariant?.quantityAvailable,
    selectedVariant?.attributes,
    secondaryVariant?.quantityAvailable,
    currentProduct?.variants,
  ]);
  const [isDefaultVariant, setIsDefaultVariant] = useState<boolean>(true);

  // Bundle variants data
  const [bundleVariantsData, setBundleVariantsData] = useState<
    Array<{
      id: string;
      index: number;
      title: string;
      image: string;
      productInfo: Array<{ label: string | undefined; value: string | undefined }>;
    }>
  >([]);
  const [bundleVariantsLoading, setBundleVariantsLoading] = useState(false);

  const isClearLens = selectedVariant?.landingPageBaseUrl?.includes("clear-contact-lenses");
  const isColorLens = selectedVariant?.landingPageBaseUrl?.includes("color-contact-lenses");
  const isEyeglass = selectedVariant?.landingPageBaseUrl?.includes("eyeglasses");
  const isAccessories = selectedVariant?.landingPageBaseUrl?.includes("accessories");
  const parentCategorySlug = product?.category?.parent?.slug;
  const selectedParentBrand = useMemo(
    () =>
      (getAttributeValue(
        currentProduct?.attributes ?? [],
        SaleorAttributes.PARENT_BRAND,
        null,
        "en",
      ) ?? "") as string,
    [currentProduct?.id],
  );
  const isPairEyewear = selectedParentBrand === "pair_eyewear";
  const isClipOn = isPairEyewear && parentCategorySlug === ProductTypes.accessories;

  const clipOnStyle = useMemo(() => {
    if (!(isPairEyewear && isClipOn)) return "";
    return (
      getAttributeValue(
        currentProduct?.attributes ?? [],
        SaleorAttributes.CLIPON_STYLE,
        ["attributes", "label"],
        lang
      ) ?? ""
    ) as string;
  }, [currentProduct?.id, lang, isClipOn, isPairEyewear]);
  // console.log("selectedVariant", selectedVariant)
  const clipColor = useMemo(() => {
    if (!isClipOn) return "";
    return (
      getAttributeValue(
        selectedVariant?.attributes ?? [],
        SaleorAttributes.CLIPON_FRAMECOLOR,
        ["attributes", "label"],
        lang
      ) ?? ""
    ) as string;
  }, [selectedVariant?.id, lang, isClipOn]);

  const [selectedClipOnStyle, setSelectedClipOnStyle] = useState(clipOnStyle);
  const [pairEyewearVariantTab, setPairEyewearVariantTab] = useState<"product" | "swatches">("product");

  function getSelectedVariantColorName(
    variants: variantData[] | undefined,
    selectedvariantId: string,
    isEyeglass: boolean
  ): string {
    const sv = variants?.find((v) => v.variantId === selectedvariantId);
    if (!sv) return "";
    if (isEyeglass) return sv.frameColor || "";
    if (sv.frameColor && sv.color) return `${sv.frameColor}/${sv.color}`;
    return sv.cliponFrameColor || sv.color || "";
  }

  useEffect(() => {
    if (isClipOn) {
      setSelectedClipOnStyle(clipOnStyle);
    }
  }, [clipOnStyle, isClipOn]);

  const handleChange = useCallback(
    async (e: React.ChangeEvent<HTMLSelectElement>) => {
      const selectedName = e.target.value;
      setSelectedClipOnStyle(selectedName);

      const selectedItem = distinctTopsStyles.find((item) => item.name === selectedName);
      if (!selectedItem?.id || !channel) return;

      try {
        startPageTransition();
        const data = await getVariantsByProductIdClient(selectedItem.id, channel);
        const matchingVariant = data?.variants?.find((variant: VariantItemPdp) => {
          const variantClipColor = getAttributeValue(
            variant.attributes ?? [],
            SaleorAttributes.CLIPON_FRAMECOLOR,
            ["attributes", "label"],
            lang,
          );
          return variantClipColor === clipColor;
        });

        const selectedVariantToUse = matchingVariant || data?.variants?.[0];
        const landingPageUrl = selectedVariantToUse?.landingPageBaseUrl;
        if (landingPageUrl) {
          router.push(landingPageUrl);
        } else {
          endPageTransition();
          throw new Error("No landingPageBaseUrl found");
        }
      } catch (error) {
        endPageTransition();
        throw new Error("No Matching Items found");
      }
    },
    [distinctTopsStyles, channel],
  );

  // const model = getAttributeValueAndLabel(currentProduct?.attributes, "clipon_style", ["attributes", "label"], lang);
  const model = useMemo(
    () =>
      (getAttributeValue(
        selectedVariant?.attributes ?? [],
        SaleorAttributes.MODEL,
        ["plainText"],
        "en",
      ) ?? "") as string,
    [selectedVariant],
  );
  const lensColor = getAttributeValueAndLabel(selectedVariant?.attributes, "clipon_lenscolor", ["attributes", "label"], lang);
  const indexName = getAlgoliaIndexName("", locale);

  const fetchLiveVariantDetails = useCallback(
    async (id: string) => {
      if (id) {
        // For getting latest price and quantity of selected variant
        const liveVariantDetails = await getLiveVariantDetailsClient(id, channel);
        if (liveVariantDetails && selectedVariantRef?.current?.id === liveVariantDetails?.id) {
          const ssrPricing = selectedVariantRef?.current?.pricing as VariantItemPdp["pricing"] | undefined;
          const livePricing = liveVariantDetails.pricing as VariantItemPdp["pricing"];
          const mergedPricing = ssrPricing?.isPreviewPrice ? ssrPricing : livePricing;

          setSelectedVariant({
            ...selectedVariantRef?.current,
            quantityAvailable: liveVariantDetails.quantityAvailable,
            availablePromotions: liveVariantDetails.availablePromotions,
            pricing: mergedPricing,
          });
          setCurrentProduct((prevProduct) => ({
            ...prevProduct,
            variants: (prevProduct.variants ?? []).map((variant) =>
              variant.id === liveVariantDetails.id
                ? { ...variant, quantityAvailable: liveVariantDetails.quantityAvailable }
                : variant,
            ),
          }));
        }
      }
    },
    [channel, selectedvariantId],
  );

  //hooks

  const categoryLivePreviewMapper = useMemo<CreateDataAttribute<LivePreviewProps> | undefined>(
    () =>
      createDataAttribute({
        id: layoutDetails?.categoryDocId,
        type: layoutDetails?.categoryDocType,
      }),
    [layoutDetails?.categoryDocId, layoutDetails?.categoryDocType],
  );
  const brandLivePreviewMapper = useMemo<CreateDataAttribute<LivePreviewProps> | undefined>(
    () => createDataAttribute({ id: layoutDetails?.brandDocId, type: layoutDetails?.brandDocType }),
    [layoutDetails?.brandDocId, layoutDetails?.brandDocType],
  );
  const collectionLivePreviewMapper = useMemo<CreateDataAttribute<LivePreviewProps> | undefined>(
    () =>
      createDataAttribute({
        id: layoutDetails?.collectionDocId,
        type: layoutDetails?.collectionType,
      }),
    [layoutDetails?.collectionDocId, layoutDetails?.collectionType],
  );
  const productLivePreviewMapper = useMemo<CreateDataAttribute<LivePreviewProps> | undefined>(
    () =>
      createDataAttribute({ id: layoutDetails?.productDocId, type: layoutDetails?.productDocType }),
    [layoutDetails?.productDocId, layoutDetails?.productDocType],
  );
  const handlePowerSelectionStatus = useCallback(
    (value: boolean) => setHasPowerSelected(value),
    [setHasPowerSelected],
  );

  const updateSelectedVariant = useCallback(
    (updatedVariant: VariantItemPdp) => {
      if (updatedVariant?.id) {
        setCurrentProduct((prevProduct) => ({
          ...prevProduct,
          variants: (prevProduct.variants ?? [])?.map((variant) =>
            variant.id === updatedVariant.id ? { ...variant, ...updatedVariant } : variant,
          ),
        }));
        setSelectedVariant(updatedVariant);
        selectedVariantRef.current = updatedVariant;
        setSelectedVariantId(updatedVariant?.id);
        setIsDefaultVariant(false);

        const pdpUrl =
          updatedVariant?.landingPageBaseUrl ??
          `/product/${currentProduct?.slug}/${updatedVariant?.id}`;

        updatePdpQueryParams(
          { [pdpInternalUrlQueryParams.QUERY_KEY]: pdpInternalUrlQueryParams.QUERY_VALUE },
          { history: "replace" },
          `/${locale}${pdpUrl}`,
        );
        // postCustomPageViewEvent(pdpUrl);
      }
    },
    [selectedVariant, lang, translations, transactional, locale, isLens],
  );

  const handleVariantSelection = useCallback(
    async (id: string) => {
      startPageTransition();
      try {
        const response = await fetch(
          `/api/product/${locale}/${currentProduct?.productType?.slug}/${id}`,
        );
        const variantData: VariantItemPdp = await response?.json();

        updateSelectedVariant(variantData);
      } catch (error) {
        console.error("Error updating selected variant:", error);
      } finally {
        endPageTransition();
      }
    },
    [locale, updateSelectedVariant, currentProduct?.productType?.slug],
  );

  const handleSecondaryVariantChange = useCallback(
    (id: string | null) => {
      const newSecondaryVariant = currentProduct?.variants?.find((each) => each.id === id) || undefined;
      setSecondaryVariant(newSecondaryVariant);
    },
    [currentProduct?.variants],
  );

  const handleQuantityChange = (quantity: number, isSecondaryVariant: boolean = false) => {
    if (isSecondaryVariant) {
      setSecondaryVariantQuantity(quantity);
    } else {
      setSelectedQuantity(quantity);
    }
  };

  const handleScrollToView = () => {
    const element = document.getElementById("power-prescription-panel");
    if (element) {
      element.scrollIntoView({ behavior: "smooth", inline: "center", block: "center" });
    }
  };

  const productVariantData = useMemo(() => {
    const [model_1, ...model_rest] = (attributes?.model_image?.value ?? []) as string[];
    const pImages: string[] =
      attributes.product_image?.value ?? attributes.cl_product_image?.value ?? [];

    let images: ImageType[] = pImages?.map((img) => ({
      type: "product",
      url: getCloudinaryURL(img) ?? NoImageSrc,
    }));

    if (model_1 && model_1 !== "") images?.splice(1, 0, { type: "model", url: model_1 });
    // if (model_rest?.length > 0) images?.splice(images?.length, 0, ...model_rest);
    if (model_rest?.length > 0) {
      const mImages: ImageType[] = model_rest.map((img) => ({
        type: "model",
        url: img,
      }));
      images.splice(images?.length, 0, ...mImages);
    }

    if (!currentProduct?.variants?.[variantIdx]) return undefined;

    return {
      id: selectedVariant?.id,
      title: attributes?.title?.value,
      brand: {
        name: getFieldValue(
          lang,
          currentProduct?.brand?.name,
          currentProduct?.brand?.translation?.name,
        ),
        url: getFieldValue(lang, currentProduct?.brand?.landingPageBaseUrl),
      },
      images,
      badges: currentProduct?.variants[variantIdx!]?.badgeList as BadgeItem[],
      promotions: currentProduct?.variants[variantIdx!]?.availablePromotions,
      pricing: currentProduct?.variants[variantIdx!]?.pricing,
      quantityAvailable: currentProduct?.variants[variantIdx!]?.quantityAvailable,
      isVtoEnabled: attributes?.vto_enabled?.value,
      categories: currentProduct?.category,
      framecolor: attributes?.fcCodeEnum?.value,
      shape: attributes?.shape?.value,
      fit: {
        value: attributes?.fStyleEnum?.value,
        originalValue: attributes?.fStyleEnum?.originalValue,
      },
      clPower: attributes?.clPower?.value ?? "",
      clPerPack: attributes?.clPerPackSize?.value ?? "",
      sku: currentProduct?.variants[variantIdx!]?.sku ?? "",
      ean: attributes?.sapEAN?.value ?? "",
      color: attributes?.clColorEnum ?? "",
    };
  }, [selectedVariant]);

  const bundleSkus = useMemo(() => {
    const raw = getAttributeValue(selectedVariant?.attributes ?? [], "bundle_id", null, lang);
    return typeof raw === "string" && raw.trim() ? raw.split(",").map(s => s.trim()).filter(Boolean) : [];
  }, [selectedVariant?.attributes, lang]);
  const isBundle = bundleSkus.length > 0;

  useEffect(() => {
    const fetchBundleVariantsData = async () => {
      if (!bundleSkus.length || !channel) {
        setBundleVariantsData([]);
        setBundleVariantsLoading(false);
        return;
      }

      try {
        setBundleVariantsLoading(true);
        const variants = await getVariantsBySkus(bundleSkus, channel, 100);

        const parseAbleField = [
          "shape",
          "parent_brand",
          "frameTypeEnum",
          "frameMaterialEnum",
          "frame_finish",
          "clipon_style",
          "title",
          "frame_design",
          "theme",
          "collectionName",
          "product_image",
          "cl_product_image",
          "fcCodeEnum",
          "lenseColor",
          "clipon_framecolor",
          "clipon_lenscolor",
          "uvBlock",
          "uv_block",
          "lens_features",
          "acGenderEnumCode",
          "acPrdMatEnumCode",
          ...LENS_FEATURES_ATTRIBUTES,
        ];

        const variantsData = variants.map((variantData: any, idx: number) => {
          const extractedAttributes = extractBundleVariantAttributes(
            lang,
            variantData.product?.attributes || [],
            variantData.attributes || []
          );

          const variantAttrs = getRequiredAttributes(extractedAttributes, parseAbleField);
          const itemParentCategorySlug = variantData.product?.category?.parent?.slug;
          const itemCategorySlug = variantData.product?.category?.slug;
          const isItemClipOn = isPairEyewear && (
            itemParentCategorySlug === ProductTypes.accessories ||
            itemCategorySlug === ProductTypes.accessories
          );
          const bundleAttrs = isItemClipOn
            ? Object.fromEntries(Object.entries(variantAttrs).filter(([k]) => k !== "fcCodeEnum" && k !== "lenseColor"))
            : Object.fromEntries(Object.entries(variantAttrs).filter(([k]) => k !== "clipon_framecolor" && k !== "clipon_lenscolor" && k !== "lenseColor"));
          const productInfo = extractProductDetails(productType, lang, bundleAttrs, pdpLayout);
          productInfo?.unshift({
            label: pdpLayout?.translations?.pdp?.pdp_attribute_brand,
            value:
              variantAttrs?.parent_brand?.value ||
              getFieldValue(
                lang,
                currentProduct?.brand?.name,
                currentProduct?.brand?.translation?.name,
              ),
          });

          const pImages: string[] = variantAttrs?.product_image?.value ?? variantAttrs?.cl_product_image?.value ?? [];
          const image = pImages.length > 0 ? (getCloudinaryURL(pImages[0]) ?? "") : "";

          return {
            id: variantData.id,
            index: idx,
            title: variantAttrs?.title?.value || "",
            image: image,
            productInfo: productInfo ?? [],
          };
        });

        setBundleVariantsData(variantsData);
      } catch (error) {
        console.error("Error fetching bundle variants data:", error);
        setBundleVariantsData([]);
      } finally {
        setBundleVariantsLoading(false);
      }
    };

    fetchBundleVariantsData();
  }, [bundleSkus.join(","), productType, currentProduct?.id]);



  const vtoEnabled = useMemo(() => {
    return (
      ((productVariantData?.isVtoEnabled === "true") as boolean) &&
      layoutDetails?.enableVirtualTryOn
    );
  }, [currentProduct?.variants, selectedvariantId, layoutDetails?.enableVirtualTryOn]);

  const breadcrumbData = (() => {
    let categories = { ...currentProduct?.category };
    const acc: BreadcrumbItem[] = [];

    if (parentCategories.includes(product?.category?.parent?.slug?.toLowerCase() ?? "")) {
      acc?.push({
        label:
          getFieldValue(lang, categories?.parent?.name, categories?.parent?.translation?.name) ??
          "",
        path: categories?.parent?.landingPageBaseUrl ?? "",
      });
    }

    acc?.push({
      label: getFieldValue(lang, categories?.name, categories?.translation?.name) ?? "",
      path: categories?.landingPageBaseUrl ?? "",
    });

    acc?.push({
      label: productVariantData?.title,
    });

    return acc;
  })();

  const tags = (() => {
    const slugs = [
      {
        slug: "frameMaterialEnum",
        query: "fm",
      },
      {
        slug: "shape",
        query: "s",
      },
      {
        slug: "windEnum",
        query: "cld",
      },
      {
        slug: "clMatEnum",
        query: "clm",
      },
      {
        slug: "acGenderEnumCode",
        query: "ag",
      },
      {
        slug: "accColorFamilyEnumCode",
        query: "ac",
      },
      {
        slug: "acPrdCatgEnumCode",
        query: "act",
      },
      {
        slug: "clipon_style",
        query: "cs",
      },
    ];

    const allowed = isPairEyewear
      ? isClipOn
        ? ["acGenderEnumCode", "acPrdCatgEnumCode", "clipon_style"]
        : ["frameMaterialEnum", "shape"]
      : null;

    const res = [];

    for (const key of slugs) {
      if (allowed && !allowed.includes(key.slug)) continue;
      if (attributes?.[key?.slug]) {
        res?.push({
          label: attributes?.[key?.slug]?.value,
          url: `/search?${key?.query}=${attributes?.[key?.slug]?.value}`,
        });
      }
    }

    return res;
  })();

  const lensPowers = (() => {
    if (!isLens) return {};
    const extractPowers: IPowerList[] = [];
    let selectedColor = "";

    if (!currentProduct?.variants) return;

    for (let i = 0; i < currentProduct?.variants?.length; i++) {
      const variantAttributes = extractVariantAttributes(lang, currentProduct, i);
      const attributes = getRequiredAttributes(variantAttributes, [
        "clPower",
        "clColorEnum",
        "clPerPackSize",
      ]);

      const power = attributes?.clPower?.value;
      const isPowered = parseFloat(power) !== 0;
      const overrideValue = getClPerPackSizeOverride(currentProduct?.id ?? "", isPowered);

      const packSize =
        overrideValue !== null ? overrideValue.toString() : attributes?.clPerPackSize?.value;

      extractPowers.push({
        id: currentProduct.variants[i].id,
        url: currentProduct.variants[i].landingPageBaseUrl!,
        quantity: currentProduct.variants[i].quantityAvailable,
        power,
        color: attributes?.clColorEnum?.value,
        clPerPackSize: packSize,
      });
    }

    let res: IPowerList[] = [];

    if (isClearLens) {
      // if clear lens
      res = getPowerList(extractPowers?.filter((v) => v.clPerPackSize === selectedPack) ?? []);
    } else {
      // if color lens
      res = getPowerList(extractPowers ?? []);
    }

    const map: Record<string, IPowerList[]> = {};
    res?.forEach((item) => {
      if (item?.id === selectedvariantId) {
        selectedColor = item?.color;
      }

      if (!map[item?.color]) {
        map[item?.color] = [item];
      } else {
        map[item?.color]?.push(item);
      }
    });

    if (selectedColor == "" && Object?.values(map)?.length == 0) return undefined;

    return { selectedColor, powerList: map };
  })();

  const variantsData = (() => {
    const data: Array<variantData> = [];

    if (!currentProduct?.variants) throw new Error("Variant data not found!");

    for (let i = 0; i < currentProduct?.variants?.length; i++) {
      const variantAttributes = extractVariantAttributes(lang, currentProduct, i);
      const attributes = getRequiredAttributes(variantAttributes, [
        "product_image",
        "cl_product_image",
        "lenseColor",
        "fcCodeEnum",
        "clColorEnum",
        "accColorFamilyEnumCode",
        "clPerPackSize",
        "clPower",
        "clipon_framecolor",
        "clipon_lenscolor",
        "pair_swatch_image",
      ]);
      const variant = currentProduct?.variants?.[i];
      let landingPageBaseUrl = variant.landingPageBaseUrl!;
      let images: string[] =
        attributes?.product_image?.value ?? attributes?.cl_product_image?.value;
      images = images?.map((img) => getCloudinaryURL(img) ?? NoImageSrc);
      // const colorName = parseJsonGetValue(
      //   extractValueBySlug(lang, variant.attributes ?? [], "fcCodeEnum") ??
      //     extractValueBySlug(lang, variant.attributes ?? [], "clColorEnum"),
      //   "attributes.label",
      // );
      const frameColor = attributes?.fcCodeEnum?.value;
      const colorName: string =
        (isClipOn ? attributes?.clipon_lenscolor?.value : undefined) ??
        attributes?.lenseColor?.value ??
        attributes?.clColorEnum?.value ??
        attributes?.accColorFamilyEnumCode?.value;

      // SKU Selection Logic
      if (isColorLens) {
        const zeroPowerLens = lensPowers?.powerList?.[colorName].find((v) => v.power === "0.00");

        if (zeroPowerLens) {
          if (
            zeroPowerLens?.quantity > 0 ||
            (zeroPowerLens?.quantity <= 0 &&
              currentProduct?.variants[defaultVariantIdx!].quantityAvailable <= 0)
          ) {
            landingPageBaseUrl = zeroPowerLens?.url;
          }
        }
      }

      const variantPower = attributes?.clPower?.value;
      const isPowered = parseFloat(variantPower) !== 0;
      const overrideValue = getClPerPackSizeOverride(currentProduct?.id ?? "", isPowered);
      const packSize =
        overrideValue !== null
          ? overrideValue.toString()
          : (attributes?.clPerPackSize?.value ?? "");
      const pairSwatchRaw = attributes?.pair_swatch_image?.value;
      const pairSwatchImage =
        Array.isArray(pairSwatchRaw) && pairSwatchRaw.length > 0
          ? (getCloudinaryURL(pairSwatchRaw[0]) ?? pairSwatchRaw[0])
          : typeof pairSwatchRaw === "string"
            ? (getCloudinaryURL(pairSwatchRaw) ?? pairSwatchRaw)
            : undefined;

      data?.push({
        image: images?.[0]!,
        type: images?.[1],
        landingPageBaseUrl,
        variantId: variant.id,
        quantityAvailable: variant.quantityAvailable,
        clPerPackSize: packSize,
        frameColor,
        cliponFrameColor: attributes?.clipon_framecolor?.value ?? "",
        color: colorName,
        hexcode: parseJsonGetValue(
          extractValueBySlug("en", variant?.attributes ?? [], "clipon_framecolor"),
          "attributes.hexcode",
        ) || parseJsonGetValue(
          extractValueBySlug("en", variant?.attributes ?? [], "fcCodeEnum"),
          "attributes.hexcode",
        ) || parseJsonGetValue(
          extractValueBySlug("en", variant?.attributes ?? [], "clColorEnum"),
          "attributes.hexcode",
        ),
        variantIds: [],
        ...(pairSwatchImage && { pairSwatchImage }),
      });
    }

    return data.sort((a, b) => b.quantityAvailable - a.quantityAvailable);
  })();

  const productVariants = (() => {
    if (!isLens || !lensPowers) {
      return variantsData;
    } else {
      // dedup the sorted data

      const dedupObj: Record<string, any> = {};
      variantsData.forEach((item: variantData) => {
        item.color;

        if (!dedupObj[item?.color]) {
          dedupObj[item?.color] = { ...item, image: ClSwatch?.src, variantIds: [item?.variantId] };
        } else {
          dedupObj[item?.color]["variantIds"]?.push(item?.variantId);
        }
      });

      return Object.values(dedupObj) as Array<variantData>;
    }
  })();

  const lensPackSizes = useMemo(() => {
    if (!isClearLens) return [];
    const lensPackMap = new Map<string, LensPackItem>();

    // variantsData already has override applied, so we can use it directly
    variantsData.forEach((v) => {
      if (!lensPackMap.has(v.clPerPackSize)) {
        lensPackMap.set(v.clPerPackSize, {
          packSize: v.clPerPackSize,
          quantity: v.quantityAvailable,
          variantId: v.variantId,
          landingpageBaseUrl: v.landingPageBaseUrl,
        });
      }
    });

    return Array.from(lensPackMap.values());
  }, [selectedVariant, variantsData]);

  const productSizing = (() => {
    const data = productVariantData?.fit?.value;
    if (!data) return [];

    const category = currentProduct?.category?.name?.toLocaleLowerCase() ?? "";
    const code = String(productVariantData?.fit?.originalValue)?.toLocaleLowerCase();
    const variantId = productVariantData?.id;

    return [
      {
        variantId,
        label: data,
        code,
        info: fitInfo?.[lang]?.[category]?.[code],
      },
    ];
  })();

  const selectedFit = useMemo(
    () =>
      (getAttributeValue(
        selectedVariant?.attributes ?? [],
        SaleorAttributes.FRAME_FIT,
        null,
        lang,
      ) ?? "") as string,
    [selectedVariant, lang],
  );

  const handleFitChange = async (fitKey: string) => {
    console.log({ fitKey });
    const variant = (productSizing || []).find((d) => d.code === fitKey);

    if (!variant) return;
    await handleVariantSelection(variant.variantId ?? "");
  };

  const filteredProductDetailsTab = useMemo<ProductDetailsTabs[]>(() => {
    const uniqueTabTypes = new Set<string>();
    const newTabs: ProductDetailsTabs[] = [];

    const addUniqueTabs = (tabs: ProductDetailsTabs[]) => {
      tabs.forEach((each) => {
        if (
          each.type === productDetails.SIZE_AND_FIT &&
          ![ProductType.SUNGLASSES, ProductType.EYEGLASSES].includes(productType)
        ) {
          return;
        }
        if (each.type === productDetails.CUSTOM_TAB) {
          newTabs.push(each);
        } else if (!uniqueTabTypes.has(each.type)) {
          uniqueTabTypes.add(each.type);
          newTabs.push(each);
        }
      });
    };
    if (hasArrayElements(layoutDetails?.productDetailsTabs)) {
      addUniqueTabs(layoutDetails?.productDetailsTabs);
    }
    if (hasArrayElements(layoutDetails?.categoryProductDetailsTabs)) {
      addUniqueTabs(layoutDetails?.categoryProductDetailsTabs);
    }
    if (hasArrayElements(layoutDetails?.locationProductDetailsTabs)) {
      addUniqueTabs(layoutDetails?.locationProductDetailsTabs);
    }
    return newTabs;
  }, [
    layoutDetails?.productDetailsTabs,
    layoutDetails?.categoryProductDetailsTabs,
    layoutDetails?.locationProductDetailsTabs,
    productType,
  ]);

  const productDetailsTabData = (() => {
    const allTabs =
      filteredProductDetailsTab?.map((item) => {
        return item?.enable !== false
          ? {
              enable: item.enable ?? true,
              label: item.tabTitle,
              key: item.key,
              type: item.type as productDetailsType,
              data: item,
            }
          : null;
      }) ?? [];

    const tabs: tabData[] = allTabs.filter((v) => v !== null) as tabData[];
    const sizeAndFitIdx = tabs?.findIndex((v: any) => v?.type === "sizeAndFit");

    for (let i = 0; i < (tabs?.length ?? 0); i++) {
      const tab = tabs[i];

      if (tab?.type === "about") {
        tab["data"] = {
          images:
            productVariantData?.images?.map((v) => ({
              type: v.type,
              url: getCloudinaryURL(v.url),
            })) ?? [],
          description: attributes?.description?.value,
          feature1: attributes?.feature1?.value,
          feature2: attributes?.feature2?.value,
          feature3: attributes?.feature3?.value,
          feature4: attributes?.feature4?.value,
        };
      }

      if (tab?.type === "productDetails") {
        const pdpAttributes = isClipOn
          ? Object.fromEntries(Object.entries(attributes).filter(([k]) => k !== "fcCodeEnum" && k !== "lenseColor"))
          : Object.fromEntries(Object.entries(attributes).filter(([k]) => k !== "clipon_framecolor" && k !== "clipon_lenscolor" && (isPairEyewear ? k !== "lenseColor" : true)));
        const info = extractProductDetails(productType, lang, pdpAttributes, pdpLayout);
        info?.unshift({
          label: pdpLayout?.translations?.pdp?.pdp_attribute_brand,
          value: attributes?.parent_brand?.value || productVariantData?.brand?.name,
        });

        tab["data"] = {
          images: productVariantData?.images,
          productInfo: info,
          isBundle: isBundle,
        };
      }

      if (tab?.type === "giftPackaging") {
        tab["data"] = {
          media: (filteredProductDetailsTab[i] as any)?.media,
          items: (filteredProductDetailsTab[i] as any)?.items,
        };
      }

      if (tab?.type === "customTab") {
        tab["data"] = {
          media: (filteredProductDetailsTab[i] as any).media,
          description: (filteredProductDetailsTab[i] as any).description,
        };
      }

      if (tab?.type === "shippingAndReturn") {
        tab["data"] = {
          images: productVariantData?.images,
          description: (filteredProductDetailsTab[i] as any)?.description,
        };
      }
    }

    if (sizeAndFitIdx > -1) {
      tabs?.splice(sizeAndFitIdx, 1, {
        label: filteredProductDetailsTab[sizeAndFitIdx]?.tabTitle,
        type: filteredProductDetailsTab[sizeAndFitIdx]?.type as productDetailsType,
        key: filteredProductDetailsTab[sizeAndFitIdx]?.key,
        data: {
          images: productVariantData?.images?.map((v) => getCloudinaryURL(v.url)) ?? [],
          measurementsTab: (filteredProductDetailsTab[sizeAndFitIdx] as any)?.measurementsTab,
          sizeGuideTab: (filteredProductDetailsTab[sizeAndFitIdx] as any)?.sizeGuideTab,
          lensSize: {
            key: attributes?.lensSize?.slug,
            label: attributes?.lensSize?.attribute,
            value: formatMeasurement(attributes?.lensSize?.value, lang),
          },
          tSize: {
            key: attributes?.tSize?.slug,
            label: attributes?.tSize?.attribute,
            value: formatMeasurement(attributes?.tSize?.value, lang),
          },
          bridgeSize: {
            key: attributes?.bridgeSize?.slug,
            label: attributes?.bridgeSize?.attribute,
            value: formatMeasurement(attributes?.bridgeSize?.value, lang),
          },
          fit: productVariantData?.fit?.originalValue,
        },
      });
    }
    return tabs;
  })() as unknown as ProductDetailsTabData[];

  // const PdpPromoData: PdpPromoContent[] = (() => {
  //   return [
  //     {
  //       desc: pdpLayout?.brandBanner?.info?.[lang],
  //       media: {
  //         desktop: pdpLayout?.brandBanner?.media?.desktop?.[lang] ?? "",
  //         mobile: pdpLayout?.brandBanner?.media?.mobile?.[lang] ?? "",
  //       },
  //       mediaType: "image",
  //       title: productVariantData?.brand?.name ?? "",
  //     },
  //   ];
  // })();

  const isNonReturnable = useMemo(
    () =>
      getVarientAttributeValue(
        selectedVariant?.attributes || [],
        lang,
        SaleorAttributes.IS_RETURNABLE,
      )?.value !== "1",
    [selectedVariant?.attributes, lang],
  );

  const showExchangeEyeglassesBadge = useMemo(
    () =>
      productType === ProductType.EYEGLASSES &&
      getVarientAttributeValue(
        selectedVariant?.attributes || [],
        lang,
        SaleorAttributes.IS_RETURNABLE,
      )?.value === "1",
    [productType, selectedVariant?.attributes, lang],
  );

  const CNCBadgeData = (() => {
    const res: Array<{ badge: CNCBadgeTypes; label: string }> = [];

    if (
      showExchangeEyeglassesBadge &&
      layoutDetails?.exchangeEyeglassesBadge?.title
    ) {
      res?.push({
        badge: "NotReturnable",
        label: layoutDetails.exchangeEyeglassesBadge.title,
      });
    } else if (isNonReturnable && layoutDetails?.exchangeBadge?.title) {
      res?.push({
        badge: "NotReturnable",
        label: layoutDetails?.exchangeBadge?.title,
      });
    }

    if (layoutDetails?.enableClickAndCollect) {
      res?.push({
        badge: "ClickNCollect",
        label: translations?.pdp?.pdp_click_and_collect_label ?? "",
      });
    }

    return res;
  })();

  // const handleNewCart = useCallback(
  //   async (
  //     lineItems: { variantId: string; quantity: number }[],
  //     callBackFn: (response: CheckoutResponse | null) => void,
  //   ) => {
  //     const channel = getChannelId(locale);
  //     const response = await createCart(channel, lineItems);
  //     const id = response?.checkoutCreate?.checkout?.id;
  //     if (id) {
  //       // updateCartId(id); Todo
  //       setInCookieMetadataSA(getCartIdByLocale(locale), id);
  //     }
  //     if (response?.checkoutCreate) callBackFn(response?.checkoutCreate);
  //   },
  //   [locale], //Todo updateCartId]
  // );

  const lensConfiguratorImageURL = useMemo(() => {
    let lensConfiguratorImageURL = "";
    const images = productVariantData?.images || [];

    for (let index = 0; index < images.length; index++) {
      const image = images[index];
      if (image) {
        lensConfiguratorImageURL = image.url;
        break;
      }
    }

    return lensConfiguratorImageURL;
  }, [productVariantData]);

  const handleAddToCart = async (variantId?: string, secondaryVariantId?: string, options?: { suppressToast?: boolean }) => {
    console.log(currentProduct, "product");
    console.log(productVariantData);
    console.log(variantId, selectedVariant?.id);
    if (hasLensPower(currentProduct?.productType?.slug) && !hasPowerSelected) {
      setPowerSelectionError(true);
      handleScrollToView();
      return;
    }

    setPowerSelectionError(false);

    try {
      const cartId: string = await getFromCookieMetadataSA(getCartIdByLocale(locale));

      const metadata = localSearchParams?.queryID
        ? [{ key: MetaDataKeys.QUERY_ID, value: localSearchParams?.queryID }]
        : undefined;

      const lineItems: {
        variantId: string;
        quantity: number;
        metadata?: { key: string; value: string }[];
      }[] = [
        {
          variantId: variantId ?? selectedVariant?.id ?? "",
          quantity: selectedQuantity ?? 1,
          ...(metadata ? { metadata } : {}),
        },
      ];

      if (secondaryVariant?.id) {
        lineItems?.push({
          variantId: secondaryVariantId ?? secondaryVariant?.id,
          quantity: secondaryVariantQuantity,
          ...(metadata ? { metadata } : {}),
        });
      }

      let response: CheckoutResponse | null | undefined;

      if (!cartId) {
        const channel = getChannelIdClient(locale as string);
        const languageCode = lang === "ar" ? LanguageCodeEnum.Ar : LanguageCodeEnum.En;
        const resp = await createCart(channel, languageCode, lineItems);
        console.log("creating cart", resp);
        const id = resp?.checkoutCreate?.checkout?.id;
        if (id) {
          // updateCartId(id); Todo
          console.log("cart id setting in cookie--", getCartIdByLocale(locale), id);
          await setInCookieMetadataSA(getCartIdByLocale(locale), id).catch((error) => console.error("Failed to save cart id cookie", error));
        }
        response = resp?.checkoutCreate;
      } else {
        response = await addItemsToCart(cartId, lineItems);

        const [error] = response?.errors || [];

        if (error?.code === cartErrors.NOTFOUND) {
          response = await addNewCartWithMultiVariantSupport(locale, lineItems);
        }
      }

      if (response?.checkout?.lines) {
        if (!options?.suppressToast) {
          showToast({
            imgUrl: productVariantData?.images[0].url,
            text: translations?.common?.common_added_to_cart_toast_message ?? "",
            lang,
            redirectUrl: "/cart",
          });
        }
        const count = getCartCount(response?.checkout?.lines ?? []) ?? cartCount;
        updateCartCount(count);

        algoliaAddToCartWithQueryClient(
          indexName,
          eventParam?.ADDED_TO_CART,
          variantId ?? selectedvariantId,
          localSearchParams?.queryID,
          user?.magrabi_id,
        );

        handlePostAnalytics({
          eventType: analyticEventTypes.ACTIONS,
          data: {
            target: analyticEvents.ADD_TO_CART,
            product: {
              ...currentProduct,
              name: productVariantData?.title,
              hitIndex: localSearchParams?.hitIndex,
              selectedQuantity: selectedQuantity ?? 1,
              variant_color: productVariantData?.framecolor,
            },
            selectedVariantId: selectedVariant?.id,
            buyFlow: analyticBuyFlows.STANDARD_FLOW,
            source: localSearchParams?.source,
            sourceId: localSearchParams?.sourceId,
          },
        });
      }

      if (response?.errors && response?.errors.length > 0) {
        const error = response.errors?.find(
          (error) =>
            error?.code === cartErrors?.INSUFFICIENT_STOCK_ERROR_CODE ||
            error?.code === cartErrors?.PRODUCT_UNAVAILABLE_FOR_PURCHASE_ERROR_CODE,
        );
        let errorMessage = "";
        if (error?.code === cartErrors?.INSUFFICIENT_STOCK_ERROR_CODE) {
          errorMessage =
            translations?.common?.common_add_to_cart_insufficient_stock_toast_message ?? "";
        } else if (error?.code === cartErrors?.QUANTITY_GREATER_THAN_LIMIT_ERROR_CODE) {
          errorMessage =
            translations?.common
              ?.common_add_to_cart_quantity_exceeds_checkout_limit_toast_message ?? "";
        } else if (error?.code === cartErrors?.PRODUCT_UNAVAILABLE_FOR_PURCHASE_ERROR_CODE) {
          errorMessage =
            translations?.common?.common_product_unavailable_for_purchase_toast_message ?? "";
        } else {
          errorMessage = translations?.common?.common_add_to_cart_error_toast_message ?? "";
        }

        showToast({
          imgUrl: productVariantData?.images[0]?.url,
          text: errorMessage ?? "",
          lang,
          redirectUrl: "/cart",
        });
      }
    } catch (err) {
      console.log("add to cart", err);
    } finally {
      console.log("add to cart done ");
    }
  };

  function handleResetPowerSelectionError() {
    setPowerSelectionError(false);
  }

  const handleShare = async () => {
    const params = {
      title: productVariantData?.title,
      text: productVariantData?.title,
      url: selectedVariant?.landingPageBaseUrl,
    };

    try {
      await navigator.share({
        title: params.title,
        text: params.text,
        url: params.url,
      });
    } catch (err) {
      console.error(`Error: ${err}`);
    }
  };

  const handleRemoveFromWishlist = async () => {
    setIsWishlisted(false);
    removeProductFromWishlist(selectedvariantId);

    try {
      const wishlistId: string = await getFromCookieMetadataSA(
        getWishlistIdByLocale(locale as string),
      );
      const response = await deleteWishlistLineItem(wishlistId, selectedvariantId);
      if (response?.wishlist) {
        showToast({
          text: translations?.common?.common_product_removed_from_wishlist_message ?? "",
          lang,
          icon: HeartIcon,
          // redirectUrl: isAuthenticated ? "/my-account/favorites" : "/favorites",
        });
      } else {
        showToast({
          text: translations?.common?.common_add_to_cart_error_toast_message ?? "",
          lang,
          icon: HeartIcon,
        });
        setIsWishlisted(true);
        addProductToWishlist(selectedvariantId);
      }
    } catch (error) {
      setIsWishlisted(true);
      addProductToWishlist(selectedvariantId);
      showToast({
        text: translations?.common?.common_add_to_cart_error_toast_message ?? "",
        lang,
        icon: HeartIcon,
      });
    }
  };

  const handleAddToWishlist = async () => {
    setIsWishlisted(true);
    addProductToWishlist(selectedvariantId);

    try {
      const wishlistId: string = await getFromCookieMetadataSA(
        getWishlistIdByLocale(locale as string),
      );

      if (wishlistId) {
        const response = await addToWishlist(
          wishlistId,
          selectedvariantId,
          !!isDefaultVariant,
          secondaryVariant?.id,
        );
        if (response?.wishlist?.id) {
          showToast({
            text: translations?.common?.common_product_added_to_wishlist_message!,
            lang,
            icon: HeartIcon,
            redirectUrl: isAuthenticated ? "/my-account/favorites" : "/favorites",
          });
          algoliaWishlistConversionClient(
            indexName,
            eventParam?.WISHLIST_ADD,
            selectedvariantId,
            localSearchParams?.queryID,
            user?.magrabi_id,
          );

          await setInCookieMetadataSA(getWishlistIdByLocale(locale as string), response?.wishlist?.id).catch((error) => console.error("Failed to save wishlist id cookie", error));
          handlePostAnalytics({
            eventType: analyticEventTypes.ACTIONS,
            data: {
              target: analyticEvents.ADD_TO_WISHLIST,
              product: {
                ...currentProduct,
                hitIndex: localSearchParams?.hitIndex,
                selectedQuantity: 1,
                name: productVariantData?.title,
                variant_color: productVariantData?.framecolor,
              },
              selectedVariantId: variantId,
              source: localSearchParams?.source,
              sourceId: localSearchParams?.sourceId,
            },
          });
        } else {
          setIsWishlisted(false);
          removeProductFromWishlist(selectedvariantId);
        }
      } else {
        const channel = getChannelIdInClient(locale as string);
        const lines = [{ variantId: selectedvariantId, isDefaultVariant: !!isDefaultVariant }];
        if (secondaryVariant?.id)
          lines.push({ variantId: secondaryVariant.id, isDefaultVariant: !!isDefaultVariant });
        const response: WishlistCreateResponse = await createWishlist(channel, lines);
        if (response?.wishlist?.id) {
          showToast({
            text: translations?.common?.common_product_added_to_wishlist_message ?? "",
            lang,
            icon: HeartIcon,
            redirectUrl: isAuthenticated ? "/my-account/favorites" : "/favorites",
          });
          algoliaWishlistConversionClient(
            indexName,
            eventParam?.WISHLIST_ADD,
            selectedvariantId,
            localSearchParams?.queryID,
            user?.magrabi_id,
          );
          await setInCookieMetadataSA(getWishlistIdByLocale(locale as string), response?.wishlist?.id).catch((error) => console.error("Failed to save wishlist id cookie", error));
          handlePostAnalytics({
            eventType: analyticEventTypes.ACTIONS,
            data: {
              target: analyticEvents.ADD_TO_WISHLIST,
              product: {
                ...currentProduct,
                hitIndex: localSearchParams?.hitIndex,
                selectedQuantity: 1,
                name: productVariantData?.title,
                variant_color: productVariantData?.framecolor,
              },
              selectedVariantId: variantId,
              source: localSearchParams?.source,
              sourceId: localSearchParams?.sourceId,
            },
          });
        }
      }
    } catch (error) {
      setIsWishlisted(false);
      removeProductFromWishlist(selectedvariantId);
      showToast({
        text: translations?.common?.common_add_to_cart_error_toast_message ?? "",
        lang,
        icon: HeartIcon,
      });
    }
  };
  const handleSearchParams = (params: ReadonlyURLSearchParams) => {
    if (product && variantId) {
      const source = params?.get("source") ?? undefined;
      const sourceId = params?.get("sourceId") ?? undefined;
      const hitIndex = Number(params?.get("index")) || undefined;
      const queryID = params?.get("queryID") ?? undefined;
      setLocalSearchParams({ source, sourceId, hitIndex, queryID });

      handlePostAnalytics({
        eventType: analyticEventTypes?.DETAILS,
        data: {
          target: analyticEvents?.VIEW_ITEM,
          product: {
            ...currentProduct,
            hitIndex,
            selectedQuantity,
            name: productVariantData?.title,
            variant_color: productVariantData?.framecolor,
          },
          selectedVariantId: variantId,
          source,
          sourceId,
        },
      });
    }
  };

  const discountedPrice: number = useMemo(() => {
    if (!isContactLensProduct(productType)) {
      if (!secondaryVariant) {
        return getDisplayPriceAmount(selectedVariant?.pricing);
      }
      const price = getDisplayPriceAmount(selectedVariant?.pricing);
      const secondaryVariantPrice = getDisplayPriceAmount(secondaryVariant?.pricing);
      return price + secondaryVariantPrice;
    }
    const primaryPrice = getDisplayPriceAmount(selectedVariant?.pricing);
    if (!secondaryVariant) {
      return primaryPrice * selectedQuantity;
    }
    const secondaryVariantPrice = getDisplayPriceAmount(secondaryVariant?.pricing);
    return primaryPrice * selectedQuantity + secondaryVariantPrice * secondaryVariantQuantity;
  }, [selectedVariant, secondaryVariant, productType, selectedQuantity, secondaryVariantQuantity]);

  const pdpPriceInfo = useMemo(() => {
    const [availablePromotion] = selectedVariant?.availablePromotions || [];
    const { costPrice, priceUndiscounted, price } = selectedVariant?.pricing || {};
    const undiscountedPrice = costPrice?.gross?.amount ?? priceUndiscounted?.gross?.amount ?? 0;
    // VIP early-access only: show list price (no cut price). Normal promos keep discounted price.
    const isVipEarlyAccess = isFuturePromotionPreviewPricing(selectedVariant?.pricing);
    const salePrice = isVipEarlyAccess
      ? getDisplayPriceAmount(selectedVariant?.pricing)
      : (price?.gross?.amount ?? priceUndiscounted?.gross?.amount ?? 0);

    if (!isContactLensProduct(productType)) {
      return buildProductPriceInfo(undiscountedPrice, salePrice, availablePromotion, selectedVariant?.pricing);
    }

    if (!secondaryVariant) {
      return buildProductPriceInfo(
        undiscountedPrice * selectedQuantity,
        salePrice * selectedQuantity,
        availablePromotion,
        selectedVariant?.pricing,
      );
    }

    const secondaryUndiscounted =
      secondaryVariant?.pricing?.costPrice?.gross?.amount ??
      secondaryVariant?.pricing?.priceUndiscounted?.gross?.amount ??
      0;
    const secondarySale = isVipEarlyAccess
      ? getDisplayPriceAmount(secondaryVariant?.pricing)
      : (secondaryVariant?.pricing?.price?.gross?.amount ??
        secondaryVariant?.pricing?.priceUndiscounted?.gross?.amount ??
        0);

    return buildProductPriceInfo(
      undiscountedPrice * selectedQuantity + secondaryUndiscounted * secondaryVariantQuantity,
      salePrice * selectedQuantity + secondarySale * secondaryVariantQuantity,
      availablePromotion,
      selectedVariant?.pricing,
    );
  }, [selectedVariant, secondaryVariant, productType, selectedQuantity, secondaryVariantQuantity]);

  // Hide promo name badge only during VIP early-access preview (green banner only).
  const isVipEarlyAccessPreview = isFuturePromotionPreviewPricing(selectedVariant?.pricing);

  const pdpColor = productVariantData?.framecolor ?? productVariantData?.color?.value ?? "";
  const altText = generateAltText(
    productVariantData?.brand?.name || "",
    productVariantData?.title || "",
    pdpColor,
    "", // swatchesColor - not used on PDP
    lang,
  );

  const variantsByVolume = useMemo(
    () =>
      currentProduct &&
      currentProduct?.variants &&
      groupByAttributes(currentProduct.variants, [SaleorAttributes.ACC_VOLUME], lang),
    [productVariants, lang],
  );
  const selectedVolume = useMemo(
    () =>
      (getAttributeValue(selectedVariant?.attributes, SaleorAttributes.ACC_VOLUME, null, lang) ??
        "") as string,
    [selectedVariant, lang],
  );

  // const undiscountedPrice: number = useMemo(() => {
  //   const { costPrice, priceUndiscounted } = selectedVariant?.pricing || {};
  //   if (!secondaryVariant) {
  //     return costPrice?.gross?.amount ?? priceUndiscounted?.gross?.amount ?? 0;
  //   }
  //   const price = costPrice?.gross?.amount ?? priceUndiscounted?.gross?.amount ?? 0;
  //   const secondaryVariantPrice =
  //     secondaryVariant?.pricing?.costPrice?.gross?.amount ??
  //     secondaryVariant?.pricing?.priceUndiscounted?.gross?.amount ??
  //     0;
  //   return price + secondaryVariantPrice;
  // }, [selectedVariant, secondaryVariant]);

  // const { hasDiscount, discountValue, percent } = useMemo(() => {
  //   const [availablePromotion] = selectedVariant?.availablePromotions || [];
  //   return { ...getCalculatedPriceInfo(undiscountedPrice, discountedPrice, availablePromotion) };
  // }, [discountedPrice, selectedVariant, undiscountedPrice]);

  useEffect(() => {
    if (recommendationsVariantRef.current === selectedvariantId) return;
    recommendationsVariantRef.current = selectedvariantId;
    const fetchRecommendations = async () => {
      try {
        const productRecommendations = await getRecommendationProducts(
          [selectedvariantId],
          layoutDetails?.recommendations,
          locale,
        );
        setRecommendedProducts(productRecommendations);
      } catch (error) {
        setRecommendedProducts([]);
      }
    };

    fetchRecommendations();
  }, [selectedvariantId, locale]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    handleSearchParams(searchParams);
  }, [searchParams]);

  useEffect(() => {
    setIsWishlisted(wishlistedProducts?.includes(selectedvariantId));
  }, [selectedvariantId, wishlistedProducts]);

  useEffect(() => {
    fetchLiveVariantDetails(selectedvariantId);
  }, [fetchLiveVariantDetails, selectedvariantId]);

  // Contact lens out-of-stock is now derived via useMemo above — no useEffect needed

  useEffect(() => {
    const handleAnalytics = async () => {
      algoliaViewedEventClient(
        algoliaEventParam.PRODUCT_VIEW,
        indexName ?? "",
        eventParam.PRODUCT_VIEW,
        variantId,
        user?.magrabi_id,
      );
    };
    handleAnalytics();
  }, [indexName, user?.magrabi_id, variantId]);

  const uniqueBadges = Array.from(
    new Map(productVariantData?.badges?.map((item) => [item?.name, item])).values(),
  );
  const isSecondaryOutOfStock = secondaryVariant
    ? (secondaryVariant.quantityAvailable ?? 0) <= 0
    : false;

  return (
    <section>
      <Breadcrumb className="ps-4 lg:ps-12" items={breadcrumbData} />
      <Spacer space={"mb-[2.4375rem] md:mb-[4.5rem]"}>
        <div className="container px-4 md:px-12">
          <PromotionPreviewNotice
            language={lang}
            pricing={selectedVariant?.pricing}
            translations={translations}
            availablePromotions={selectedVariant?.availablePromotions}
          />
        </div>
        <div className="container relative flex flex-col gap-4 md:flex-row lg:gap-12 xl:gap-16">
          <div className="flex-1">
            <ProductDisplayCarousel
              isOutOfStock={isOutOfStock}
              variantId={selectedVariant?.id ?? ""}
              images={productVariantData?.images ?? []}
              className="top-[100px] md:sticky"
              isVtoEnabled={vtoEnabled}
              outOfStockLabel={translations?.pdp?.pdp_out_of_stock_label}
              frameId={{
                sku: productVariantData?.sku ?? "",
                ean: productVariantData?.ean,
              }}
              handleShare={handleShare}
              wishlistProps={{
                handleAddToWishlist,
                handleRemoveFromWishlist,
                isWishlisted,
                setIsWishlisted,
              }}
              altText={altText}
              translations={layoutDetails?.translations}
            />
          </div>
          <div className="grid md:w-1/2 md:pe-12 lg:w-[45%]">
            <div className="order-2 mt-7 flex gap-3 max-md:mx-5 md:order-1 md:mt-0 md:pb-4">
              {/* {productVariantData && productVariantData?.badges?.length > 0 ? ( */}
              <div className="flex flex-wrap items-start gap-3 md:pt-0">
                {(isLens
                  ? isOutOfStock
                  : (selectedVariant?.quantityAvailable ?? 0) <= 0 ||
                    isSecondaryOutOfStock ||
                    isOutOfStock) && (
                  <ProductBadge
                    variant={"outOfStock"}
                    isPdpScreen={true}
                    title={translations?.plp?.plp_out_of_stock_label ?? "Out of Stock"}
                  />
                )}
                {uniqueBadges.map((item, idx: number) => {
                  return (
                    <ProductBadge
                      isPdpScreen={true}
                      key={idx}
                      title={getFieldValue(lang, item?.name, item?.translation?.name) || ""}
                      variant={item?.name as any}
                      containerClass="font-semibold"
                    />
                  );
                })}
              </div>
              {/* ) : (
                <></>
              )} */}
            </div>
            <div className="order-2 border-b-8 pb-7 pt-4 md:border-b md:pb-6 md:pt-0">
              <div className="flex flex-col gap-[6px] max-md:px-5">
                <h1>
                  <CLink
                    href={productVariantData?.brand?.url ?? ""}
                    className="text-base font-medium md:text-xl"
                  >
                    {productVariantData?.brand?.name}
                  </CLink>
                  <span className="block text-sm md:text-base">{productVariantData?.title}</span>
                </h1>
                <ProductTags data={tags} className="pt-2 text-sm" />
              </div>
            </div>
            <div className="order-3 border-b-8 py-7 md:border-b md:py-6">
              <div className="max-md:px-5">
                <ProductPrice
                  className="hidden text-[18px] md:flex"
                  locale={locale}
                  priceInfo={pdpPriceInfo}
                  translations={layoutDetails?.translations}
                  productType={productType}
                />

                {selectedVariant?.pricing?.price?.tax?.amount > 0 && (
                  <>
                    <p className="hidden text-sm text-tertiary-6 md:block">
                      {translations?.pdp?.pdp_includes_vat_label_for_large_screen}
                    </p>
                    <p className="text-sm text-tertiary-6 md:hidden">
                      {translations?.pdp?.pdp_includes_vat_label_for_small_screen}
                    </p>
                  </>
                )}

                {selectedVariant?.pricing?.costPrice?.gross?.amount >= 1 && (
                  <div className="pt-1 md:pt-[2px]">
                    <PaymentOptions
                      discountedPrice={discountedPrice}
                      lang={lang}
                      locale={locale}
                      selectedVariant={selectedVariant}
                      translations={translations}
                      installments={layoutDetails?.installments}
                    />
                  </div>
                )}

                {selectedVariant?.pricing?.costPrice?.gross?.amount >= 1 && !isVipEarlyAccessPreview && (
                  <div className="pt-2 md:pt-[6px]">
                    <ProductPromotion promotions={productVariantData?.promotions!} lang={lang} />
                  </div>
                )}
                <CNCBadge className="pt-4 md:hidden" badges={CNCBadgeData} />
              </div>
            </div>

            <CNCBadge
              className="order-4 hidden border-b py-6 max-md:px-5 md:flex"
              badges={CNCBadgeData}
            />

            {/* {isPairEyewear && isClipOn && (
              <div className="order-1 md:order-5  mx-auto md:mx-0 my-4 flex items-center justify-between gap-2 rounded-[10px] bg-[#fafafa] px-4 py-6 text-[14px] w-[90%]">
                <div className="flex flex-row md:items-center gap-6"> */}
            {/* First Selector */}
            {/* <div className="flex flex-col md:flex-row md:items-center mb-4 md:mb-0">
                    <label className="mb-2 md:mb-0">{model.label} :</label>

                    <select
                      className="mx-[10px] py-0 text-[14px] outline-none"
                      value={selectedClipOnStyle}
                      onChange={handleChange}
                    >
                      {(distinctTopsStyles ?? []).map((item: ClipOnStyleOption) => (
                        <option key={item.id} value={item.name}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                  </div> */}
            {/* Second Selector (replaced with plain label) */}
            {/* {lensColor.value && 
                  <div className="flex flex-col md:flex-row md:items-center">
                    <label className="mb-2 md:mb-0">{lensColor.label} :</label> */}

            {/*
                        <select className="py-0 text-[14px] outline-none">
                          <option value="transparent">Crystal Clear</option>
                          <option value="colored">Colored</option>
                        </select>
                        */}
            {/* <span className="text-[14px] text-gray-800 mx-[10px]">
                      {lensColor.value}
                    </span>
                  </div>
                  }
                </div>
              </div>
            )} */}

            {!isClearLens && !(product?.productType?.slug === ProductType.ACC_VOLUME) && (
              <div className="order-1 mt-6 border-b-8 md:order-5 md:mt-4 md:border-none">
                <div className="max-md:px-5">
                  {(!isLens || (isLens && (productVariants?.length ?? 0) > 0)) && (
                    <p className="mb-1">
                      <span className="text-[13px] font-semibold">
                        {fillSlot(
                          (productVariants?.length ?? 0) > 1
                            ? (translations.pdp?.pdp_color_variant_count_multiple_label ?? "")
                            : (translations.pdp?.pdp_color_variant_count_single_label ?? ""),
                          "{{count}}",
                          productVariants?.length ?? 0
                        )}{" "}
                        :{" "}
                      </span>
                      <span className="text-[13px] font-normal text-tertiary-6">
                        {getSelectedVariantColorName(
                          productVariants,
                          selectedvariantId,
                          isEyeglass ?? false
                        )}
                      </span>
                    </p>
                  )}
                  {isPairEyewear && !isBundle ? (
                    <>
                      <div className="border-b border-[#D4D4D4]">
                        <div className="flex gap-2 px-3">
                          <button
                            type="button"
                            onClick={() => setPairEyewearVariantTab("product")}
                            className={cn(
                              "border-b-2 px-1 pb-2 pt-1 text-[13px] font-medium transition-colors",
                              pairEyewearVariantTab === "product"
                                ? "border-black"
                                : "border-transparent"
                            )}
                          >
                            {translations?.pdp?.pdp_variant_view_product_label}
                          </button>
                          <button
                            type="button"
                            onClick={() => setPairEyewearVariantTab("swatches")}
                            className={cn(
                              "border-b-2 px-1 pb-2 pt-1 text-[13px] font-medium transition-colors",
                              pairEyewearVariantTab === "swatches"
                                ? "border-black"
                                : "border-transparent"
                            )}
                          >
                            {translations?.pdp?.pdp_variant_view_swatches_label}
                          </button>
                        </div>
                      </div>
                      {pairEyewearVariantTab === "product" && (
                        <div className="my-3">
                          <ProductColorVariants
                            isLens={isLens && lensPowers !== undefined}
                            isEyeglass={isEyeglass ?? false}
                            isContactLense={isColorLens ?? false}
                            hasPowerSelected={hasPowerSelected}
                            selectedVariantQuantity={selectedVariant?.quantityAvailable ?? 0}
                            powerList={lensPowers?.powerList}
                            lang={lang}
                            selectedvariantId={selectedvariantId}
                            variantsData={productVariants}
                            isOutOfStock={isOutOfStock}
                            brandName={productVariantData?.brand?.name || ""}
                            productName={productVariantData?.title || ""}
                          />
                        </div>
                      )}
                      {pairEyewearVariantTab === "swatches" && (
                        <PairEyewearSwatches
                          variantsData={productVariants ?? []}
                          selectedvariantId={selectedvariantId}
                        />
                      )}
                    </>
                  ) : (
                    <ProductColorVariants
                      isLens={isLens && lensPowers !== undefined}
                      isEyeglass={isEyeglass ?? false}
                      isContactLense={isColorLens ?? false}
                      hasPowerSelected={hasPowerSelected}
                      selectedVariantQuantity={selectedVariant?.quantityAvailable ?? 0}
                      powerList={lensPowers?.powerList}
                      lang={lang}
                      selectedvariantId={selectedvariantId}
                      variantsData={productVariants}
                      isOutOfStock={isOutOfStock}
                      brandName={productVariantData?.brand?.name || ""}
                      productName={productVariantData?.title || ""}
                    />
                  )}
                </div>
                <div className="max-md:px-5">
                  {productSizing.length > 0 && (
                    <ProductSizing
                      className="pt-3"
                      items={productSizing}
                      label={translations?.pdp?.pdp_fit_variant_label ?? ""}
                      translations={translations}
                      selectedFit={selectedFit}
                      onSelectFit={handleFitChange}
                    />
                  )}
                </div>
                {product?.productType?.slug === ProductType.EYEGLASSES &&
                  translations?.pdp?.pdp_dont_know_prescription_link &&
                  translations?.pdp?.pdp_book_eye_test_link &&
                  !["sa", "eg"].includes(getCountryCodeFromLocale(locale)) && (
                    <div className="order-5 mt-4 hidden items-center gap-4 md:flex">
                      <span className="text-[0.813rem] font-bold">
                        {translations?.pdp?.pdp_dont_know_prescription_link}
                      </span>
                      <CLink
                        href={`/${locale}/book-eyetest/type`}
                        className="flex items-center gap-2 underline"
                      >
                        <Image src={IconEye} alt="Book eye test icon" />
                        <span className="text-[0.813rem] text-[#666666]">
                          {translations?.pdp?.pdp_book_eye_test_link}
                        </span>
                      </CLink>
                    </div>
                  )}
              </div>
            )}

            {isAccessories &&
              product?.productType?.slug === ProductType.ACC_VOLUME &&
              variantsByVolume &&
              // Object.keys(variantsByVolume)?.length > 1 &&
              selectedVariant && (
                <div className="border-b-8 md:order-5 md:border-b md:py-5">
                  <div className="max-md:px-5">
                    <VolumeSelector
                      handleSizeChange={(volumeKey) =>
                        handleVariantSelection(variantsByVolume[volumeKey][0].id)
                      }
                      label={translations?.pdp?.pdp_volume_variant_label ?? ""}
                      selectedSize={selectedVolume}
                      selectedVariant={selectedVariant}
                      variants={variantsByVolume}
                    />
                  </div>
                </div>
              )}
            {isLens && isClearLens && lensPackSizes.length > 0 && (
              <div className="border-b-8 md:order-5 md:border-b md:py-5">
                <div className="max-md:px-5">
                  <LensePackSize
                    lang={lang}
                    selectedVariantId={selectedvariantId}
                    data={lensPackSizes}
                    multipleLabel={
                      translations?.pdp?.pdp_no_of_lens_count_per_box_multiple_label ?? ""
                    }
                    singleLabel={translations?.pdp?.pdp_no_of_lens_count_per_box_single_label ?? ""}
                    title={attributes?.clPerPackSize?.attribute!}
                    // handleClick={setSelectedVariantId}
                    handleVariantselection={handleVariantSelection}
                    handlePackSelection={setSelectedPack}
                    selectedPack={selectedPack}
                  />
                </div>
              </div>
            )}
            {isLens && lensPowers && (
              <LensPowerSelection
                classname="order-6"
                powerList={lensPowers?.powerList ?? {}}
                isClLens={productVariants.length > 1}
                translations={translations}
                selectedColor={lensPowers?.selectedColor ?? ""}
                handleVariantSelection={handleVariantSelection}
                handleSecondaryVariantSelection={handleSecondaryVariantChange}
                powerSelectionProps={{
                  handlePowerSelectionStatus,
                  hasPowerSelected,
                  hasPowerSelectionError,
                }}
                quantitySelectorProps={{
                  handleQuantityChange,
                  quantity: selectedQuantity,
                  secondaryVariantQuantity: secondaryVariantQuantity,
                }}
                handleResetPowerSelectionError={handleResetPowerSelectionError}
                secondarySelectedVariant={secondaryVariant}
                selectedVariant={selectedVariant}
              />
            )}
            {/* {product?.productType?.slug === ProductType.EYEGLASSES &&
              translations?.pdp?.pdp_dont_know_prescription_link &&
              translations?.pdp?.pdp_book_eye_test_link && (
                <div className="order-5  mt-4 flex items-center gap-4 md:hidden max-md:px-5 ">
                  <span className="text-[0.813rem] font-bold">
                    {translations?.pdp?.pdp_dont_know_prescription_link}
                  </span>
                  <CLink href="/book-eyetest/type" className="flex items-center gap-2 underline">
                    <Image src={IconEye} alt="Boot eye test icon" />
                    <span className="text-[0.813rem] text-[#666666]">
                      {translations?.pdp?.pdp_book_eye_test_link}
                    </span>
                  </CLink>
                </div>
              )} */}

            <AddToCart
              locale={locale}
              product={currentProduct}
              productImage={productVariantData?.images[0]?.url}
              variant={selectedVariant}
              lensConfiguratorImageURL={lensConfiguratorImageURL}
              secondaryVariant={secondaryVariant ?? undefined}
              translations={translations}
              pairEyewearTopsContent={pdpLayout.pairEyewearTopsContent}
              quantity={productVariantData?.quantityAvailable!}
              label={translations?.pdp?.pdp_add_to_cart_button ?? ""}
              findStoreLabel={translations?.pdp?.pdp_find_a_store_label || ""}
              outOfStockLabel={translations?.pdp?.pdp_notify_when_in_stock_title ?? ""}
              priceInfo={{
                ...pdpPriceInfo,
                currency: "",
              }}
              className="order-7 md:border-t"
              handleAddToCart={(_v, _sv, options) => handleAddToCart(selectedVariant?.id, undefined, options)}
              lang={lang}
              handleShare={handleShare}
              wishlistProps={{
                handleAddToWishlist,
                handleRemoveFromWishlist,
                isWishlisted,
                setIsWishlisted,
              }}
              hitIndex={localSearchParams?.hitIndex}
              source={localSearchParams?.source ?? ""}
              sourceId={localSearchParams?.sourceId ?? ""}
              productType={productType}
              outOfStock={isOutOfStock}
              handlePowerSelectionStatus={handlePowerSelectionStatus}
              handleVariantSelection={handleVariantSelection}
              powerList={lensPowers?.powerList ?? {}}
              selectedColor={lensPowers?.selectedColor ?? ""}
              isClLens={isColorLens ?? false}
              isLensProduct={isLens}
              isFree={selectedVariant?.pricing?.costPrice?.gross?.amount < 1}
              isPairEyewear={isPairEyewear}
              isBundle={isBundle}
              isClipOn={isClipOn}
              distinctTopsStyles={distinctTopsStyles}
              model={model}
              brand={currentProduct?.brand?.name ?? ""}
            />

            <USPElements items={layoutDetails.uspConfiguration} className="order-8 max-md:px-5" />
            <div className="order-9 flex flex-wrap items-center gap-3 py-4 max-md:px-5 md:py-5">
              <span className="text-[13px] font-bold">
                {translations?.pdp?.pdp_need_help_label ?? ""}
              </span>
              <span className="flex cursor-pointer items-center gap-[6px] md:gap-2">
                <Image
                  src={WpIcon}
                  alt={lang === "en" ? "Whatsapp" : "واتساب"}
                  width={16}
                  height={16}
                  className="h-4 w-4"
                />
                <CLink
                  href={whatsappLinks[getCountryCodeFromLocale(locale)]}
                  className="text-[13px] text-tertiary-6 underline"
                  target="_blank"
                >
                  {translations?.pdp?.pdp_contact_on_whatsapp_link ?? ""}
                </CLink>
              </span>
            </div>
          </div>
        </div>
      </Spacer>
      {productDetailsTabData.length > 0 ? (
        <Spacer space="mb-[4.5rem] md:mb-[6rem]">
          <div id="productDetails" className="invisible h-0 w-0"></div>
          <ProductDetailsTab
            data={productDetailsTabData}
            lang={lang}
            category={currentProduct?.category?.name ?? ""}
            layoutDetails={pdpLayout}
            // categoryURL={
            //   product?.category?.landingPageBaseUrl ||
            //   `/search/${productListingPageKey.categoryListing}/${product?.category?.slug}`
            // }
            categoryURL={`/${locale}/search/?cat=${product?.category?.slug}`}
            altText={altText}
            bundleVariantsData={bundleVariantsData}
            bundleVariantsLoading={bundleVariantsLoading}
          />
        </Spacer>
      ) : (
        <></>
      )}
      {recommendedProducts?.length > 0 ? (
        <Spacer space="mb-[4.5rem] md:mb-[6rem]">
          <ProductCarousel
            translations={translations}
            products={recommendedProducts}
            title={layoutDetails?.recommendations?.title}
            tryOn={layoutDetails?.enableVirtualTryOn}
            source={analyticEventSources.PDP}
          />
        </Spacer>
      ) : (
        <></>
      )}

      {/* <PdpPromoTile
          variant="image"
          contents={PdpPromoData}
          visible={pdpLayout?.brandBanner?.info?.[lang] != undefined}
        /> */}

      <Spacer space="mb-[4.5rem] md:mb-[6rem]">
        <PdpBanner
          categoryBanner={layoutDetails?.categoryBanner}
          brandBanner={layoutDetails?.brandBanner}
          productBanner={layoutDetails?.productBanner}
          collectionBanner={layoutDetails?.collectionBanner}
          locale={locale}
          translations={translations}
          categoryLivePreviewMapper={categoryLivePreviewMapper?.scope(previewConstants.PDP_BANNER)}
          brandLivePreviewMapper={brandLivePreviewMapper?.scope(previewConstants.PDP_BANNER)}
          collectionLivePreviewMapper={collectionLivePreviewMapper?.scope(
            previewConstants.PDP_BANNER,
          )}
          productLivePreviewMapper={productLivePreviewMapper?.scope(previewConstants.PDP_BANNER)}
        />
      </Spacer>

      <BenefitSection benefitsData={pdpLayout?.benefits} />
    </section>
  );
};

export default PdpLayoutDetails;